import React from 'react';
import { connect } from 'react-redux';
import { AnyAction, ThunkDispatch } from '@reduxjs/toolkit';

import { UserAccountId } from 'api/UserAccount/model/UserAccountId';

import { ConnectedUserButtonApp } from 'apps/UserSessionApp/UserButtonApp';
import { ISignUpData } from 'shared/components/SignUpOrSignIn/ISignUpData';
import { SignUpOptionsFormActions } from 'shared/components/SignUpOrSignIn/SignUpOptions/actions';
import { IActiveFormState } from 'shared/components/SignUpOrSignIn/SignUpOptions/reducers';
import { ConnectedSignUpOptions } from 'shared/components/SignUpOrSignIn/SignUpOptions/SignUpOptions';

import { SignUpAndLogInButtons } from './SignUpAndLoginButtons/SignUpAndLogInButtons';

import { SignUpAndLoginButtonsActions, SignUpAndLoginDispatch } from './SignUpAndLoginButtons/actions';
import { ISignUpAndLoginButtonsState } from './SignUpAndLoginButtons/reducers';

import { DjangoApiManager } from 'shared/api/DjangoApiManager';
import { RuntimeException } from 'shared/lib/general/exceptions/RuntimeException';
import { Observer } from 'shared/utils/observer';
import { PathUtils } from 'shared/utils/pathUtils';
import { URLStringUtils } from 'shared/utils/urlStringUtils';

import 'shared/css/MakeItSpin.scss';
import 'shared/css/PublicSite.scss';
import { IExtraArguments } from 'shared/components/Provider';

interface IPublicSiteProps {
    signUpAndLoginButtonsState : ISignUpAndLoginButtonsState;
}

interface IConnectedPublicSiteProps extends IPublicSiteProps {
    dispatch : SignUpAndLoginDispatch & ThunkDispatch<IPublicSiteProps, IExtraArguments, AnyAction>;
}

const PUBLIC_SITE_MOUNT_ELEMENT_ID = 'public-site-app'; // Note: This should match the ID of the mount element for this component because we've decided to set the needed data attribute there
const INITIAL_ACTIVE_FORM_NAME = 'data-initial-active-form';
const CLICK_SELECTOR_CLASS : string = 'public-site-sign-up-modal-trigger';
const DASHBOARD_REDIRECT_URL : string = 'https://app.bevspot.com/account/dashboard';

const djangoApiManager = new DjangoApiManager();

class PublicSite extends React.Component<IConnectedPublicSiteProps, object> {

    private defaultInitialActiveFormName : IActiveFormState = 'signUpAndCreateRetailerForm';
    private initialActiveFormName : IActiveFormState = 'signUpAndCreateRetailerForm';
    private availableForms : Array<IActiveFormState> = ['signUpAndCreateRetailerForm', 'signUpForm', 'signInForm'];

    public UNSAFE_componentWillMount() {
        const signUpOptionsFormMountElement : HTMLElement | null = document.getElementById(PUBLIC_SITE_MOUNT_ELEMENT_ID);

        if (signUpOptionsFormMountElement) {
            const initialActiveFormAttributeValue = signUpOptionsFormMountElement.getAttribute(INITIAL_ACTIVE_FORM_NAME);
            signUpOptionsFormMountElement.removeAttribute(INITIAL_ACTIVE_FORM_NAME);

            if (initialActiveFormAttributeValue !== null) {
                if (this.availableForms.indexOf(initialActiveFormAttributeValue as IActiveFormState) > -1) {
                    this.defaultInitialActiveFormName = initialActiveFormAttributeValue as IActiveFormState;
                } else {
                    djangoApiManager.logJsError(
                        'Could not get initial active form name data attribute. Found element with Id: ' +
                            PUBLIC_SITE_MOUNT_ELEMENT_ID + '. Was expecting attribute: ' + INITIAL_ACTIVE_FORM_NAME + '. Found value: ' + initialActiveFormAttributeValue,
                        null,
                        new RuntimeException('Could not get initial active form name data attribute'),
                        true
                    );
                }
            }
        } else {
            djangoApiManager.logJsError(
                'Could not find mount point for sign up form. Was expecting ' + PUBLIC_SITE_MOUNT_ELEMENT_ID,
                null,
                new RuntimeException('Could not find mount point for sign up form'),
                true
            );
        }
    }

    public componentDidMount() {
        const openSignUpModal = (event : JQueryEventObject) => {
            const {
                dispatch,
                signUpAndLoginButtonsState
            } = this.props;

            if (typeof(signUpAndLoginButtonsState.loggedInUser) === 'undefined') {
                return;
            }

            if (signUpAndLoginButtonsState.loggedInUser === null) {
                const triggerSource = event.target.getAttribute('data-trigger-source') || null;

                const newURL = URLStringUtils.replaceOrRemoveURLParameter(window.location.href, 'triggerSource', triggerSource);
                window.history.replaceState(null, '', newURL);

                const initialActiveFormAttributeValue = event.target.getAttribute(INITIAL_ACTIVE_FORM_NAME);

                if (initialActiveFormAttributeValue !== null && this.availableForms.indexOf(initialActiveFormAttributeValue as IActiveFormState) > -1) {
                    this.initialActiveFormName = initialActiveFormAttributeValue as IActiveFormState;
                } else {
                    this.initialActiveFormName = this.defaultInitialActiveFormName;
                }

                dispatch(SignUpOptionsFormActions.onSetActiveForm(this.initialActiveFormName));
                dispatch(SignUpOptionsFormActions.onSetFormIsShown(true));
                Observer.observeAction('Opened SignUpOrSignInModal', { active : this.initialActiveFormName });
            } else {
                window.location.href = PathUtils.getAbsolutePathForRequest('/start_free_trial');
            }

            return false;
        };

        $(document).on('click.' + CLICK_SELECTOR_CLASS, '.' + CLICK_SELECTOR_CLASS, openSignUpModal);
    }

    public componentWillUnmount() {
        $(document).off('click.' + CLICK_SELECTOR_CLASS, '.' + CLICK_SELECTOR_CLASS);
    }

    public render() {
        const {
            signUpAndLoginButtonsState,
        } = this.props;

        const loadingSpinner = <span className="bevicon bevico-spinner2 make-it-spin loading-spinner"/>;

        if (typeof(signUpAndLoginButtonsState.loggedInUser) === 'undefined') {
            return loadingSpinner;
        }

        if (signUpAndLoginButtonsState.loggedInUser === null) {
            return (
                <span>
                    <SignUpAndLogInButtons
                        onClickSignUp={ this.onSignUpAndLogInButtonsClickSignUp }
                        onClickLogIn={ this.onSignUpAndLogInButtonsClickLogIn }
                    />
                    <ConnectedSignUpOptions
                        availableFormOptions={ ['signUpAndCreateRetailerForm', 'signInForm'] }
                        isEmbeddedInModal={ true }
                        isDismissible={ true }
                        formIsVisibleOnLoad={ false }
                        additionalUserFieldsAreShown={ false }
                        additionalRetailerFieldsAreShown={ false }
                        additionalRetailerFieldsAreRequired={ false }
                        onSuccessfulSignIn={ this.onConnectedSignUpOptionsSuccessfulSignIn }
                        onSuccessfulSignUp={ this.onConnectedSignUpOptionsSuccessfulSignUp }
                    />
                </span>
            );
        }

        if (signUpAndLoginButtonsState.loggedInUser.userInfo === null) {
            return loadingSpinner;
        }

        // TODO: do we care about this `retailerDashboardLinkVisible` logic? right now it's not being used in `ConnectedUserButtonApp`
        return (
            <span className="user-icon-table">
                <a className="user-icon-label" onClick={ this.onUserIconWithPopoverIconClick } >My Account</a>
                <span className="user-icon-container">
                    <ConnectedUserButtonApp />
                </span>
            </span>
        );
    }

    private readonly onConnectedSignUpOptionsSuccessfulSignIn = () => {
        return this.props.dispatch(SignUpAndLoginButtonsActions.onSuccessfulSignIn())
        .then(() => {
            window.location.href = DASHBOARD_REDIRECT_URL;
        });
    }

    private readonly onConnectedSignUpOptionsSuccessfulSignUp = (userAccountIdentifier : UserAccountId, signUpData : ISignUpData) => {
        return this.props.dispatch(SignUpAndLoginButtonsActions.onSuccessfulSignUp(userAccountIdentifier, signUpData))
        .then(() => {
            window.location.href = DASHBOARD_REDIRECT_URL;
        });
    }

    private readonly onSignUpAndLogInButtonsClickSignUp = () => {
        this.initialActiveFormName = this.defaultInitialActiveFormName;
        this.props.dispatch(SignUpOptionsFormActions.onSetActiveForm(this.defaultInitialActiveFormName));
        this.props.dispatch(SignUpOptionsFormActions.onSetFormIsShown(true));
        Observer.observeAction('Opened SignUpOrSignInModal', { active : this.defaultInitialActiveFormName });
    }

    private readonly onSignUpAndLogInButtonsClickLogIn = () => {
        this.initialActiveFormName = this.defaultInitialActiveFormName;
        this.props.dispatch(SignUpOptionsFormActions.onSetActiveForm('signInForm'));
        this.props.dispatch(SignUpOptionsFormActions.onSetFormIsShown(true));
        Observer.observeAction('Opened SignUpOrSignInModal', { active : 'signInForm' });
    }

    private readonly onUserIconWithPopoverIconClick = () => {
        this.props.dispatch(SignUpAndLoginButtonsActions.onSetUserIconPopoverIsShown(!this.props.signUpAndLoginButtonsState.userIconPopoverIsShown));
        Observer.observeAction('User icon clicked', { popoverIsShown : this.props.signUpAndLoginButtonsState.userIconPopoverIsShown });
    }
}

const mapStateToProps = (state : IPublicSiteProps) : IPublicSiteProps => {
    return {
        signUpAndLoginButtonsState: state.signUpAndLoginButtonsState,
    };
};

export const ConnectedPublicSite = connect<IPublicSiteProps, object, object, IPublicSiteProps>(mapStateToProps)(PublicSite);
